import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, FileText, Users, Receipt, Edit, Trash2, Plus, Copy, Clock } from 'lucide-react';

const actionConfig = {
  create: { label: 'Creation', color: 'bg-emerald-100 text-emerald-700', icon: Plus },
  update: { label: 'Modification', color: 'bg-blue-100 text-blue-700', icon: Edit },
  delete: { label: 'Suppression', color: 'bg-red-100 text-red-700', icon: Trash2 },
  duplicate: { label: 'Duplication', color: 'bg-purple-100 text-purple-700', icon: Copy },
};

const entityConfig = {
  invoice: { label: 'Facture', icon: FileText },
  quote: { label: 'Devis', icon: FileText },
  client: { label: 'Client', icon: Users },
  expense: { label: 'Depense', icon: Receipt },
};

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');

  useEffect(() => { loadLogs(); }, [filterAction, filterEntity]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAction) params.append('action', filterAction);
      if (filterEntity) params.append('entity_type', filterEntity);
      params.append('limit', '100');
      const res = await api.get(`/audit-log?${params.toString()}`);
      setLogs(res.data);
    } catch (err) { console.error('Failed to load audit logs:', err); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Journal d'activite</h1>
          <p className="text-sm text-zinc-400 mt-1">{logs.length} evenement(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterAction} onValueChange={v => setFilterAction(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="create">Creation</SelectItem>
              <SelectItem value="update">Modification</SelectItem>
              <SelectItem value="delete">Suppression</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterEntity} onValueChange={v => setFilterEntity(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="invoice">Factures</SelectItem>
              <SelectItem value="quote">Devis</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="expense">Depenses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
          <p>Aucune activite enregistree</p>
        </div>
      ) : (
        <div className="space-y-1">
          {logs.map((log, i) => {
            const act = actionConfig[log.action] || actionConfig.create;
            const ent = entityConfig[log.entity_type] || { label: log.entity_type, icon: FileText };
            const ActIcon = act.icon;
            return (
              <div key={i} className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${act.color}`}>
                  <ActIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">
                    <span className="text-zinc-400">{log.user_email}</span>
                    {' — '}
                    <Badge className={`${act.color} text-xs mr-1`}>{act.label}</Badge>
                    <span className="text-zinc-300">{ent.label}</span>
                    {log.details && <span className="text-zinc-500 ml-1">({log.details})</span>}
                  </p>
                </div>
                <span className="text-xs text-zinc-600 flex-shrink-0">{new Date(log.created_at).toLocaleString('fr-CH')}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
